import { useMemo, useState, useEffect, useRef, useCallback, memo } from "react";
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    TableSortLabel, 
    Paper, 
    Checkbox, 
    TextField, 
    InputAdornment,
    Select,
    MenuItem,
    Button,
    IconButton,
    Autocomplete,
    Box,
    Tooltip,
    Popper,
    ClickAwayListener,
    Tabs,
    Tab
} from "@mui/material";
import GlobalIcon from "../GlobalIcon/GlobalIcon";
import { downloadFile } from "../../api/fileDownloader";
import styles from "./CustomTable.module.css";

// Memoized dropdown list component for performance
const DropdownList = memo(({ columnKey, allValues, searchText, selectedValues, onToggle }) => {
    const filteredValues = useMemo(() => {
        if (!searchText) return allValues;
        const searchLower = searchText.toLowerCase();
        return allValues.filter(v => v.toLowerCase().includes(searchLower));
    }, [allValues, searchText]);

    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

    const handleItemClick = useCallback((value, e) => {
        // Don't toggle if clicking directly on checkbox (it handles its own change)
        if (e.target.type === 'checkbox' || e.target.closest('input[type="checkbox"]') || e.target.closest('span[role="checkbox"]')) {
            return;
        }
        onToggle(columnKey, value);
    }, [columnKey, onToggle]);

    const handleCheckboxChange = useCallback((e, value) => {
        e.stopPropagation();
        onToggle(columnKey, value);
    }, [columnKey, onToggle]);

    if (filteredValues.length === 0) {
        return <div className={styles["dropdown-empty"]}>No matching values</div>;
    }

    return (
        <>
            {filteredValues.map((value) => {
                const isChecked = selectedSet.has(value);
                return (
                    <div
                        key={value}
                        className={styles["dropdown-item"]}
                        onClick={(e) => handleItemClick(value, e)}
                    >
                        <Checkbox
                            checked={isChecked}
                            size="small"
                            className={styles["dropdown-checkbox"]}
                            onChange={(e) => handleCheckboxChange(e, value)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                '&.Mui-checked': {
                                    color: 'rgba(79, 70, 229, 0.60)',
                                },
                                '&.Mui-checked .MuiSvgIcon-root': {
                                    fill: 'rgba(79, 70, 229, 0.60)',
                                    color: 'rgba(79, 70, 229, 0.60)',
                                },
                            }}
                        />
                        <span className={styles["dropdown-item-text"]}>{value}</span>
                    </div>
                );
            })}
        </>
    );
});

DropdownList.displayName = 'DropdownList';

function CustomTable({ 
    columns = [], 
    data = [], 
    onRowSelect = null,
    selectedRows = [],
    rowKey = "id",
    enableSearch = true,
    enableSorting = true,
    enableSelection = true,
    enableShowEntries = true,
    enableFilter = true,
    enableColumnFilters = true,
    enableExport = true,
    enablePagination = true,
    showEntriesOptions = [10, 25, 50, 100],
    defaultEntriesPerPage = 100,
    searchPlaceholder = "Search",
    onExport = null,
    exportType = null, // Export type for file download (e.g., "Holdings", "Orders", "BulkOrders")
    onFilter = null,
    className = "",
    enableRecentSearch = true,
    recentSearchStorageKey = "customTable_recentSearches",
    maxRecentSearches = 10,
    columnsWithDropdown = [], // Array of column keys that should have dropdown filter (e.g., ['broker_client_code', 'exchange_trading_symbol'])
    secondaryTabs = null, // Array of {label, value} for secondary tabs
    activeSecondaryTab = null, // Current active secondary tab value
    onSecondaryTabChange = null, // Callback function for tab change
    // Date filter props
    enableDateFilter = false,
    dateFilterOptions = [], // Array of {label, value} for date filter options
    dateFilterValue = "",
    onDateFilterChange = null,
    // Strategy filter props
    enableStrategyFilter = false,
    strategyFilterOptions = [], // Array of {label, value} for strategy filter options
    strategyFilterValue = "",
    onStrategyFilterChange = null
}) {
    const POSITIVE_COLOR = "#419E6A";
    const NEGATIVE_COLOR = "#D83232";

    const [globalSearch, setGlobalSearch] = useState("");
    const [columnFilters, setColumnFilters] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [selectedRowIds, setSelectedRowIds] = useState(selectedRows || []);
    const [entriesPerPage, setEntriesPerPage] = useState(defaultEntriesPerPage);
    const [currentPage, setCurrentPage] = useState(1);
    const [recentSearches, setRecentSearches] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState({}); // Track which column dropdowns are open
    const [dropdownSearch, setDropdownSearch] = useState({}); // Track search text in each dropdown
    const anchorRefs = useRef({}); // Refs for dropdown positioning
    const prevSecondaryTabRef = useRef(activeSecondaryTab);

    // Memoize unique column values to avoid recalculation
    const uniqueColumnValues = useMemo(() => {
        const cache = {};
        columnsWithDropdown.forEach(columnKey => {
            const values = new Set();
            data.forEach(row => {
                const value = row[columnKey];
                if (value !== null && value !== undefined && value !== '') {
                    values.add(String(value));
                }
            });
            cache[columnKey] = Array.from(values).sort();
        });
        return cache;
    }, [data, columnsWithDropdown]);

    // Memoize dropdown filter check
    const dropdownFilterMap = useMemo(() => {
        return new Set(columnsWithDropdown);
    }, [columnsWithDropdown]);

    // Helper function to check if a value is numeric
    const isNumeric = useCallback((value) => {
        if (value === null || value === undefined) return false;
        return !isNaN(value) && !isNaN(parseFloat(value));
    }, []);

    // Helper function to parse numeric range filters
    const parseNumericFilter = useCallback((filterValue, cellValue) => {
        if (!isNumeric(cellValue)) return false;
        
        const numValue = parseFloat(cellValue);
        const filter = filterValue.trim();
        const filterLen = filter.length;
        
        // Early return for empty filter
        if (filterLen === 0) return false;
        
        // Range: "100-500"
        if (filterLen > 2 && filter.includes('-') && filter[0] !== '>' && filter[0] !== '<') {
            const dashIndex = filter.indexOf('-');
            const min = parseFloat(filter.substring(0, dashIndex));
            const max = parseFloat(filter.substring(dashIndex + 1));
            if (!isNaN(min) && !isNaN(max)) {
                return numValue >= min && numValue <= max;
            }
        }
        
        // Greater than or equal: ">=100"
        if (filterLen >= 3 && filter.startsWith('>=')) {
            const threshold = parseFloat(filter.substring(2));
            return !isNaN(threshold) && numValue >= threshold;
        }
        // Greater than: ">100"
        if (filterLen >= 2 && filter.startsWith('>')) {
            const threshold = parseFloat(filter.substring(1));
            return !isNaN(threshold) && numValue > threshold;
        }
        
        // Less than or equal: "<=500"
        if (filterLen >= 3 && filter.startsWith('<=')) {
            const threshold = parseFloat(filter.substring(2));
            return !isNaN(threshold) && numValue <= threshold;
        }
        // Less than: "<500"
        if (filterLen >= 2 && filter.startsWith('<')) {
            const threshold = parseFloat(filter.substring(1));
            return !isNaN(threshold) && numValue < threshold;
        }
        
        // Exact match for numbers
        const exact = parseFloat(filter);
        return !isNaN(exact) && numValue === exact;
    }, [isNumeric]);

    // Helper function to parse advanced search operators
    const parseAdvancedSearch = useCallback((searchTerm, cellValue) => {
        if (cellValue == null || cellValue === '') return false;
        
        const cellStr = String(cellValue).toLowerCase();
        const search = searchTerm.trim();
        const searchLen = search.length;
        
        if (searchLen === 0) return true;
        
        const searchLower = search.toLowerCase();
        
        // Exact match: "exact phrase"
        if (searchLen > 2 && search[0] === '"' && search[searchLen - 1] === '"') {
            return cellStr === search.slice(1, -1).toLowerCase();
        }
        
        // Wildcard: *pattern*
        if (search.includes('*')) {
            try {
                const regexPattern = search
                    .replace(/\*/g, '.*')
                    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                    .replace(/\\\*/g, '.*');
                return new RegExp(`^${regexPattern}$`, 'i').test(cellStr);
            } catch (e) {
                return cellStr.includes(search.replace(/\*/g, '').toLowerCase());
            }
        }
        
        // AND logic: term1 AND term2
        if (searchLower.includes(' and ')) {
            const terms = search.split(/\s+and\s+/i);
            return terms.every(term => cellStr.includes(term.trim().toLowerCase()));
        }
        
        // OR logic: term1 OR term2
        if (searchLower.includes(' or ')) {
            const terms = search.split(/\s+or\s+/i);
            return terms.some(term => cellStr.includes(term.trim().toLowerCase()));
        }
        
        // NOT logic: term1 NOT term2
        if (searchLower.includes(' not ')) {
            const parts = search.split(/\s+not\s+/i);
            if (parts.length === 2) {
                return cellStr.includes(parts[0].trim().toLowerCase()) && 
                       !cellStr.includes(parts[1].trim().toLowerCase());
            }
        }
        
        // Default: simple includes
        return cellStr.includes(searchLower);
    }, []);

    // Check if a column should have dropdown filter
    const hasDropdownFilter = useCallback((columnKey) => {
        return dropdownFilterMap.has(columnKey);
    }, [dropdownFilterMap]);

    // Helper function to highlight search matches in text
    const highlightText = useCallback((text, searchTerm) => {
        if (!searchTerm || !text) return text;
        
        // If text is already a React element, return as-is
        if (typeof text === 'object' && text !== null && !Array.isArray(text)) {
            return text;
        }
        
        const textStr = String(text);
        const search = searchTerm.trim();
        const cleanSearch = search.replace(/^"|"$/g, '');
        
        if (!cleanSearch) return textStr;
        
        // Simple highlighting without complex regex for performance
        const searchLower = cleanSearch.toLowerCase();
        const textLower = textStr.toLowerCase();
        const index = textLower.indexOf(searchLower);
        
        if (index === -1) return textStr;
        
        const before = textStr.substring(0, index);
        const match = textStr.substring(index, index + cleanSearch.length);
        const after = textStr.substring(index + cleanSearch.length);
        
        return (
            <>
                {before}
                <mark style={{ backgroundColor: '#FFEB3B', padding: '0 2px' }}>{match}</mark>
                {after}
            </>
        );
    }, []);

    // Load recent searches from localStorage on mount
    useEffect(() => {
        if (enableRecentSearch && recentSearchStorageKey) {
            try {
                const stored = localStorage.getItem(recentSearchStorageKey);
                if (stored) {
                    const parsed = JSON.parse(stored);
                    setRecentSearches(Array.isArray(parsed) ? parsed : []);
                }
            } catch (error) {
                console.error("Error loading recent searches:", error);
            }
        }
    }, [enableRecentSearch, recentSearchStorageKey]);

    // Sync internal state with prop when it changes externally
    useEffect(() => {
        setSelectedRowIds(selectedRows || []);
    }, [selectedRows]);

    // Clear selection when secondary tab changes (different tab = different data)
    useEffect(() => {
        if (secondaryTabs == null || secondaryTabs.length === 0) return;
        if (prevSecondaryTabRef.current !== activeSecondaryTab) {
            prevSecondaryTabRef.current = activeSecondaryTab;
            setSelectedRowIds([]);
            if (onRowSelect) {
                onRowSelect([], []);
            }
        }
    }, [activeSecondaryTab, secondaryTabs, onRowSelect]);

    // Reset to page 1 when entries per page changes
    useEffect(() => {
        setCurrentPage(1);
    }, [entriesPerPage]);

    // Handle row selection
    const getSelectedRowsByIds = useCallback((ids) => {
        if (!ids || ids.length === 0) return [];
        // Use original data so selection is stable across filters/pagination
        const idSet = new Set(ids);
        return data.filter((row) => idSet.has(row[rowKey]));
    }, [data, rowKey]);

    // Memoize column filter keys and filter state for faster lookup
    const columnFilterKeys = useMemo(() => Object.keys(columnFilters), [columnFilters]);
    const hasColumnFilters = useMemo(() => columnFilterKeys.length > 0, [columnFilterKeys]);
    const hasGlobalSearch = useMemo(() => Boolean(globalSearch?.trim()), [globalSearch]);
    
    // Memoize dropdown filter sets for O(1) lookup
    const dropdownFilterSets = useMemo(() => {
        const sets = {};
        columnFilterKeys.forEach(colKey => {
            const filterValue = columnFilters[colKey];
            if (Array.isArray(filterValue) && filterValue.length > 0) {
                sets[colKey] = new Set(filterValue);
            }
        });
        return sets;
    }, [columnFilters, columnFilterKeys]);

    // Memoize sort comparison function
    const sortCompare = useCallback((a, b, sortKey, direction) => {
        const aValue = a[sortKey];
        const bValue = b[sortKey];
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (typeof aValue === "number" && typeof bValue === "number") {
            return direction === "asc" ? aValue - bValue : bValue - aValue;
        }
        
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        return direction === "asc" 
            ? aStr.localeCompare(bStr)
            : bStr.localeCompare(aStr);
    }, []);

    // Filter and sort data
    const processedData = useMemo(() => {
        // Early return if no filters
        if (!hasGlobalSearch && !hasColumnFilters) {
            // Only sort if needed
            if (enableSorting && sortConfig.key) {
                return [...data].sort((a, b) => sortCompare(a, b, sortConfig.key, sortConfig.direction));
            }
            return data;
        }

        const searchTerm = hasGlobalSearch ? globalSearch.trim() : '';
        const hasColumnSearch = searchTerm.includes(':');
        let matchingColumn = null;
        let columnSearchValue = '';

        // Pre-process column-specific search
        if (hasColumnSearch) {
            const colonIndex = searchTerm.indexOf(':');
            const colKey = searchTerm.substring(0, colonIndex).trim();
            columnSearchValue = searchTerm.substring(colonIndex + 1).trim();
            matchingColumn = columns.find(col => 
                col.key.toLowerCase() === colKey.toLowerCase() || 
                col.label.toLowerCase() === colKey.toLowerCase()
            );
        }

        const filtered = data.filter((row) => {
            // Global search optimization
            if (hasGlobalSearch) {
                if (hasColumnSearch && matchingColumn) {
                    // Column-specific search
                    const cellValue = row[matchingColumn.key];
                    if (isNumeric(cellValue)) {
                        if (!parseNumericFilter(columnSearchValue, cellValue)) return false;
                    } else {
                        if (!parseAdvancedSearch(columnSearchValue, cellValue)) return false;
                    }
                } else if (hasColumnSearch) {
                    // Column not found, search all
                    let found = false;
                    for (let i = 0; i < columns.length; i++) {
                        const col = columns[i];
                        const cellValue = row[col.key];
                        if (isNumeric(cellValue)) {
                            if (parseNumericFilter(columnSearchValue, cellValue)) {
                                found = true;
                                break;
                            }
                        } else if (parseAdvancedSearch(columnSearchValue, cellValue)) {
                            found = true;
                            break;
                        }
                    }
                    if (!found) return false;
                } else {
                    // Regular global search
                    let found = false;
                    for (let i = 0; i < columns.length; i++) {
                        const col = columns[i];
                        const cellValue = row[col.key];
                        if (isNumeric(cellValue)) {
                            if (parseNumericFilter(searchTerm, cellValue)) {
                                found = true;
                                break;
                            }
                        }
                        if (parseAdvancedSearch(searchTerm, cellValue)) {
                            found = true;
                            break;
                        }
                    }
                    if (!found) return false;
                }
            }

            // Column filters optimization - only check columns with filters
            if (hasColumnFilters) {
                for (let i = 0; i < columnFilterKeys.length; i++) {
                    const colKey = columnFilterKeys[i];
                    const filterValue = columnFilters[colKey];
                    if (!filterValue) continue;
                    
                    const cellValue = row[colKey];
                    
                    // Handle dropdown multi-select (O(1) lookup with Set)
                    if (Array.isArray(filterValue)) {
                        if (filterValue.length > 0) {
                            const filterSet = dropdownFilterSets[colKey];
                            if (filterSet && !filterSet.has(String(cellValue))) {
                                return false;
                            }
                        }
                        continue;
                    }
                    
                    // Numeric filter
                    if (isNumeric(cellValue)) {
                        if (!parseNumericFilter(filterValue, cellValue)) return false;
                        continue;
                    }
                    
                    // Text filter
                    if (!parseAdvancedSearch(filterValue, cellValue)) return false;
                }
            }

            return true;
        });

        // Sorting (only if needed)
        if (enableSorting && sortConfig.key && filtered.length > 1) {
            filtered.sort((a, b) => sortCompare(a, b, sortConfig.key, sortConfig.direction));
        }

        return filtered;
    }, [data, hasGlobalSearch, globalSearch, hasColumnFilters, columnFilterKeys, columnFilters, dropdownFilterSets, columns, sortConfig, enableSorting, parseNumericFilter, parseAdvancedSearch, isNumeric, sortCompare]);

    // Pagination (memoized)
    const totalPages = useMemo(() => Math.ceil(processedData.length / entriesPerPage), [processedData.length, entriesPerPage]);
    const startIndex = useMemo(() => (currentPage - 1) * entriesPerPage, [currentPage, entriesPerPage]);
    const endIndex = useMemo(() => startIndex + entriesPerPage, [startIndex, entriesPerPage]);
    const paginatedData = useMemo(() => {
        return enablePagination 
            ? processedData.slice(startIndex, endIndex)
            : processedData;
    }, [enablePagination, processedData, startIndex, endIndex]);

    // Memoize selected IDs set for O(1) lookup
    const selectedIdsSet = useMemo(() => new Set(selectedRowIds), [selectedRowIds]);

    const handleSelectAll = useCallback((event) => {
        if (event.target.checked) {
            const allIds = paginatedData.map((row) => row[rowKey]);
            setSelectedRowIds(allIds);
            if (onRowSelect) {
                onRowSelect(allIds, getSelectedRowsByIds(allIds));
            }
        } else {
            setSelectedRowIds([]);
            if (onRowSelect) {
                onRowSelect([], []);
            }
        }
    }, [paginatedData, rowKey, onRowSelect, getSelectedRowsByIds]);

    const handleSelectRow = useCallback((rowId) => {
        const isSelected = selectedIdsSet.has(rowId);
        const newSelected = isSelected
            ? selectedRowIds.filter((id) => id !== rowId)
            : [...selectedRowIds, rowId];
        setSelectedRowIds(newSelected);
        if (onRowSelect) {
            onRowSelect(newSelected, getSelectedRowsByIds(newSelected));
        }
    }, [selectedRowIds, selectedIdsSet, onRowSelect, getSelectedRowsByIds]);

    // Handle sorting (memoized)
    const handleSort = useCallback((columnKey) => {
        setSortConfig((prev) => {
            let direction = "asc";
            if (prev.key === columnKey && prev.direction === "asc") {
                direction = "desc";
            }
            return { key: columnKey, direction };
        });
    }, []);

    // Save search to recent searches (memoized)
    const saveToRecentSearches = useCallback((searchTerm) => {
        if (!enableRecentSearch || !searchTerm || !searchTerm.trim()) return;
        
        const trimmedTerm = searchTerm.trim();
        setRecentSearches((prev) => {
            // Remove duplicates and add to beginning
            const filtered = prev.filter((item) => item !== trimmedTerm);
            const updated = [trimmedTerm, ...filtered].slice(0, maxRecentSearches);
            
            // Save to localStorage
            try {
                if (recentSearchStorageKey) {
                    localStorage.setItem(recentSearchStorageKey, JSON.stringify(updated));
                }
            } catch (error) {
                console.error("Error saving recent searches:", error);
            }
            
            return updated;
        });
    }, [enableRecentSearch, maxRecentSearches, recentSearchStorageKey]);

    // Remove a recent search (memoized)
    const handleRemoveRecentSearch = useCallback((event, searchTerm) => {
        event.stopPropagation();
        setRecentSearches((prev) => {
            const updated = prev.filter((item) => item !== searchTerm);
            
            // Update localStorage
            try {
                if (recentSearchStorageKey) {
                    localStorage.setItem(recentSearchStorageKey, JSON.stringify(updated));
                }
            } catch (error) {
                console.error("Error updating recent searches:", error);
            }
            
            return updated;
        });
    }, [recentSearchStorageKey]);

    const handleGlobalSearchChange = useCallback((value) => {
        setGlobalSearch(value);
        setCurrentPage(1);
    }, []);

    const handleClearSearch = useCallback(() => {
        setGlobalSearch("");
        setCurrentPage(1);
    }, []);

    // Handle when user selects from autocomplete or presses Enter
    const handleSearchSubmit = useCallback((value) => {
        if (value && value.trim()) {
            saveToRecentSearches(value);
        }
    }, [saveToRecentSearches]);

    const handleColumnFilterChange = useCallback((columnKey, value) => {
        setColumnFilters((prev) => {
            if (value) {
                return { ...prev, [columnKey]: value };
            }
            const updated = { ...prev };
            delete updated[columnKey];
            return updated;
        });
        setCurrentPage(1);
    }, []);

    // Handle dropdown checkbox selection
    const handleDropdownValueToggle = useCallback((columnKey, value) => {
        setColumnFilters((prev) => {
            const currentSelection = Array.isArray(prev[columnKey]) ? prev[columnKey] : [];
            const valueIndex = currentSelection.indexOf(value);
            
            if (valueIndex >= 0) {
                // Remove value
                if (currentSelection.length === 1) {
                    const updated = { ...prev };
                    delete updated[columnKey];
                    return updated;
                }
                const newSelection = [...currentSelection];
                newSelection.splice(valueIndex, 1);
                return { ...prev, [columnKey]: newSelection };
            } else {
                // Add value
                return { ...prev, [columnKey]: [...currentSelection, value] };
            }
        });
        setCurrentPage(1);
    }, []);

    // Handle dropdown open/close - ensure only one dropdown is open at a time
    const handleDropdownToggle = useCallback((columnKey) => {
        setDropdownOpen((prev) => {
            const isCurrentlyOpen = prev[columnKey];
            // If opening this dropdown, close all others first
            if (!isCurrentlyOpen) {
                setDropdownSearch((p) => ({ ...p, [columnKey]: '' }));
                return { [columnKey]: true };
            }
            // If closing, just close this one
            const newState = { ...prev };
            newState[columnKey] = false;
            return newState;
        });
    }, []);

    // Handle dropdown search text change
    const handleDropdownSearchChange = useCallback((columnKey, searchText) => {
        setDropdownSearch((prev) => ({ ...prev, [columnKey]: searchText }));
    }, []);

    const handleExport = useCallback(() => {
        // If exportType is provided, use fileDownloader
        if (exportType) {
            const url = `/api/analytics/download-excel/${exportType}/`;
            downloadFile(url);
        }
        
        // Also call onExport callback if provided (for backward compatibility)
        if (onExport) {
            onExport(processedData, selectedRowIds);
        }
    }, [exportType, onExport, processedData, selectedRowIds]);

    const handleFilterClick = useCallback(() => {
        if (onFilter) {
            onFilter({});
        }
    }, [onFilter]);

    // Selection checks using Set for O(1) lookup
    const isRowSelected = useCallback((rowId) => selectedIdsSet.has(rowId), [selectedIdsSet]);
    
    const isAllSelected = useMemo(() => {
        if (paginatedData.length === 0) return false;
        return paginatedData.every(row => selectedIdsSet.has(row[rowKey]));
    }, [paginatedData, selectedIdsSet, rowKey]);
    
    const isIndeterminate = useMemo(() => {
        if (selectedRowIds.length === 0 || isAllSelected) return false;
        return paginatedData.some(row => selectedIdsSet.has(row[rowKey]));
    }, [selectedRowIds.length, isAllSelected, paginatedData, selectedIdsSet, rowKey]);

    // Reusable sort icon component
    const SortIconComponent = useCallback(() => (
        <GlobalIcon iconName="TableSortIcon" className={styles["sort-icon"]} />
    ), []);

    // Get cell styling from column config
    const getCellStyle = (column, value, row) => {
        if (column.styles) {
            if (typeof column.styles === "function") {
                return column.styles(value, row);
            }
            return column.styles;
        }
        return {};
    };

    const renderCellValue = useCallback((col, row) => {
        const value = row[col.key];

        if (col.render) {
            return col.render(value, row);
        }

        const isProfitColumn = col.key === "pnl" || col.key === "pnl_pct";
        if (isProfitColumn) {
            const numeric = typeof value === "number" ? value : Number(value);
            const color = !Number.isNaN(numeric) && numeric < 0 ? NEGATIVE_COLOR : POSITIVE_COLOR;
            const display =
                col.key === "pnl_pct"
                    ? Number.isNaN(numeric)
                        ? value ?? "-"
                        : `${numeric.toFixed(2)}%`
                    : typeof value === "number"
                    ? value.toLocaleString("en-IN")
                    : value ?? "-";

            // Apply highlighting if search is active (only for text, not styled spans)
            return <span style={{ color }}>{display}</span>;
        }

        if (typeof value === "number") {
            const formatted = value.toLocaleString("en-IN");
            const highlighted = globalSearch ? highlightText(formatted, globalSearch) : formatted;
            // If highlighting returned an array/element, wrap it, otherwise return as string
            if (typeof highlighted === 'object') {
                return <span>{highlighted}</span>;
            }
            return highlighted;
        }

        const textValue = value ?? "-";
        const highlighted = globalSearch ? highlightText(textValue, globalSearch) : textValue;
        // If highlighting returned an array/element, return it, otherwise return as string
        if (typeof highlighted === 'object') {
            return highlighted;
        }
        return highlighted;
    }, [globalSearch, highlightText]);

    // Simple calculations - no memoization needed
    const startEntry = processedData.length === 0 ? 0 : startIndex + 1;
    const endEntry = Math.min(endIndex, processedData.length);

    // Memoize pagination handlers
    const handlePreviousPage = useCallback(() => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    }, []);

    const handleNextPage = useCallback(() => {
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
    }, [totalPages]);

    const handleEntriesPerPageChange = useCallback((e) => {
        setEntriesPerPage(e.target.value);
    }, []);

    // Memoize filterOptions for Autocomplete
    const filterAutocompleteOptions = useCallback((options, state) => {
        // Show all recent searches when input is empty or matches
        if (!state.inputValue) {
            return options;
        }
        // Filter options that match the input
        return options.filter(option =>
            option.toLowerCase().includes(state.inputValue.toLowerCase())
        );
    }, []);

    return (
        <div className={styles["custom-table-wrapper"]}>
            {/* Secondary Tabs */}
            {secondaryTabs && secondaryTabs.length > 0 && (
                <div className={styles["secondary-tabs-container"]}>
                    <Tabs
                        value={activeSecondaryTab || false}
                        onChange={onSecondaryTabChange}
                        className={styles["secondary-tabs"]}
                    >
                        {secondaryTabs.map((tab) => (
                            <Tab
                                key={tab.value}
                                label={tab.label}
                                value={tab.value}
                                className={styles["secondary-tab"]}
                                disableRipple
                            />
                        ))}
                    </Tabs>
                </div>
            )}
            {/* Table Controls */}
            <div className={styles["table-controls"]}>
                <div className={styles["controls-left"]}>
                    {enableShowEntries && (
                        <Select
                            value={entriesPerPage}
                            onChange={handleEntriesPerPageChange}
                            className={styles["show-entries-select"]}
                            size="small"
                            renderValue={(value) => (
                                <div className={styles["show-entries-content"]}>
                                    <span className={styles["show-entries-text"]}>Show Entries</span>
                                    <span className={styles["show-entries-value"]}>{value}</span>
                                    <GlobalIcon iconName="TableSortIcon" className={styles["show-entries-icon"]} />
                                </div>
                            )}
                            IconComponent={() => null}
                            MenuProps={{
                                PaperProps: {
                                    className: styles["show-entries-dropdown-paper"]
                                },
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'right',
                                },
                                disableAutoFocusItem: true,
                                transitionDuration: 0
                            }}
                        >
                            {showEntriesOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                    {enableSearch && (
                        <div className={styles["search-container"]}>
                            {enableRecentSearch ? (
                                <Autocomplete
                                    freeSolo
                                    openOnFocus
                                    options={recentSearches}
                                    value={globalSearch}
                                    filterOptions={filterAutocompleteOptions}
                                    onInputChange={(event, newValue) => {
                                        handleGlobalSearchChange(newValue);
                                    }}
                                    onChange={(event, newValue) => {
                                        const value = typeof newValue === 'string' ? newValue : newValue || '';
                                        handleGlobalSearchChange(value);
                                        if (value) {
                                            handleSearchSubmit(value);
                                        }
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' && globalSearch.trim()) {
                                            handleSearchSubmit(globalSearch);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            placeholder={searchPlaceholder}
                                            className={styles["search-input"]}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (
                                                    <>
                                                        <InputAdornment position="start">
                                                            <GlobalIcon iconName="SearchIcon" className={styles["search-icon"]} />
                                                        </InputAdornment>
                                                        {params.InputProps.startAdornment}
                                                    </>
                                                ),
                                                endAdornment: globalSearch ? (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Clear search">
                                                            <IconButton
                                                                size="small"
                                                                onClick={handleClearSearch}
                                                                className={styles["clear-search-button"]}
                                                                edge="end"
                                                            >
                                                                <span className={styles["clear-icon"]}>×</span>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ) : null,
                                                disableUnderline: true,
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box
                                            component="li"
                                            {...props}
                                            className={styles["recent-search-item"]}
                                        >
                                            <GlobalIcon 
                                                iconName="SearchIcon" 
                                                className={styles["recent-search-icon"]} 
                                            />
                                            <span className={styles["recent-search-text"]}>
                                                {option}
                                            </span>
                                            <IconButton
                                                size="small"
                                                className={styles["recent-search-remove"]}
                                                onClick={(e) => handleRemoveRecentSearch(e, option)}
                                                onMouseDown={(e) => e.preventDefault()}
                                            >
                                                <span className={styles["remove-icon"]}>×</span>
                                            </IconButton>
                                        </Box>
                                    )}
                                    ListboxProps={{
                                        className: styles["recent-searches-listbox"],
                                    }}
                                    PaperComponent={({ children, ...other }) => (
                                        <Paper {...other} className={styles["recent-searches-dropdown"]}>
                                            {recentSearches.length > 0 && (
                                                <div className={styles["recent-searches-header"]}>
                                                    Recent Search
                                                </div>
                                            )}
                                            {children}
                                        </Paper>
                                    )}
                                    className={styles["search-autocomplete"]}
                                    noOptionsText={recentSearches.length === 0 ? "" : "No recent searches"}
                                />
                            ) : (
                                <TextField
                                    size="small"
                                    placeholder={searchPlaceholder}
                                    value={globalSearch}
                                    onChange={(e) => handleGlobalSearchChange(e.target.value)}
                                    className={styles["search-input"]}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <GlobalIcon iconName="SearchIcon" className={styles["search-icon"]} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: globalSearch ? (
                                            <InputAdornment position="end">
                                                <Tooltip title="Clear search">
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleClearSearch}
                                                        className={styles["clear-search-button"]}
                                                        edge="end"
                                                    >
                                                        <span className={styles["clear-icon"]}>×</span>
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        ) : null,
                                        disableUnderline: true,
                                    }}
                                />
                            )}
                        </div>
                    )}
                    {enableFilter === true && (
                        <Button
                            variant="outlined"
                            onClick={handleFilterClick}
                            className={styles["control-button"]}
                            endIcon={<GlobalIcon iconName="ChevronDownIcon" className={styles["button-icon"]} />}
                        >
                            Filter
                        </Button>
                    )}
                    {enableExport && (
                        <Button
                            variant="outlined"
                            onClick={handleExport}
                            className={styles["control-button"]}
                            endIcon={<GlobalIcon iconName="DownloadIcon" className={styles["button-icon"]} />}
                        >
                            Export
                        </Button>
                    )}
                </div>
                <div className={styles["controls-right"]}>
                    {enableDateFilter && (
                        <Select
                            value={dateFilterValue}
                            onChange={(e) => onDateFilterChange && onDateFilterChange(e.target.value)}
                            className={styles["table-filter-select"]}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) {
                                    return <span className={styles["filter-placeholder"]}>Date Filter</span>;
                                }
                                const option = dateFilterOptions.find(opt => opt.value === selected);
                                return option ? option.label : selected;
                            }}
                            MenuProps={{
                                PaperProps: {
                                    className: styles["filter-menu-paper"]
                                },
                                MenuListProps: {
                                    className: styles["filter-menu-list"]
                                }
                            }}
                        >
                            {dateFilterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value} className={styles["filter-menu-item"]}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                    {enableStrategyFilter && (
                        <Select
                            value={strategyFilterValue}
                            onChange={(e) => onStrategyFilterChange && onStrategyFilterChange(e.target.value)}
                            className={styles["table-filter-select"]}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) {
                                    return <span className={styles["filter-placeholder"]}>Change Strategy</span>;
                                }
                                const option = strategyFilterOptions.find(opt => opt.value === selected);
                                return option ? option.label : selected;
                            }}
                            MenuProps={{
                                PaperProps: {
                                    className: styles["filter-menu-paper"]
                                },
                                MenuListProps: {
                                    className: styles["filter-menu-list"]
                                }
                            }}
                        >
                            {strategyFilterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value} className={styles["filter-menu-item"]}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                    {enablePagination && (
                        <div className={styles["pagination-info"]}>
                            <span className={styles["pagination-text"]}>
                                {startEntry} - {endEntry} of {processedData.length}
                            </span>
                            <div className={styles["pagination-controls"]}>
                                <IconButton
                                    size="small"
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                    className={styles["pagination-button"]}
                                >
                                    &lt;
                                </IconButton>
                                <span className={styles["pagination-page"]}>{currentPage}</span>
                                <IconButton
                                    size="small"
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                    className={styles["pagination-button"]}
                                >
                                    &gt;
                                </IconButton>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Table */}
            <TableContainer component={Paper} className={`${styles["table-container"]} ${className}`}>
                <Table className={styles["table"]} stickyHeader>
                    <TableHead>
                        <TableRow className={styles["header-row"]}>
                            {enableSelection && (
                                <TableCell className={styles["checkbox-cell"]} padding="checkbox">
                                    <Checkbox
                                        indeterminate={isIndeterminate}
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                        className={styles["checkbox"]}
                                        sx={{
                                            '&.Mui-checked': {
                                                color: 'rgba(79, 70, 229, 0.60)',
                                            },
                                            '&.Mui-checked .MuiSvgIcon-root': {
                                                fill: 'rgba(79, 70, 229, 0.60)',
                                                color: 'rgba(79, 70, 229, 0.60)',
                                            },
                                        }}
                                    />
                                </TableCell>
                            )}
                            {columns.map((col) => (
                                <TableCell
                                    key={col.key}
                                    className={styles["header-cell"]}
                                    sortDirection={sortConfig.key === col.key ? sortConfig.direction : false}
                                >
                                    {enableColumnFilters && col.filterable !== false ? (
                                        <div className={styles["header-filter-wrapper"]}>
                                            {hasDropdownFilter(col.key) ? (
                                                <>
                                                    <TextField
                                                        ref={el => anchorRefs.current[col.key] = el}
                                                        size="small"
                                                        placeholder={col.label}
                                                        value={Array.isArray(columnFilters[col.key]) && columnFilters[col.key].length > 0
                                                            ? `${columnFilters[col.key].length} selected`
                                                            : ""}
                                                        readOnly
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!dropdownOpen[col.key]) {
                                                                handleDropdownToggle(col.key);
                                                            }
                                                        }}
                                                        onFocus={(e) => {
                                                            e.stopPropagation();
                                                            if (!dropdownOpen[col.key]) {
                                                                handleDropdownToggle(col.key);
                                                            }
                                                        }}
                                                        className={styles["column-filter-input"]}
                                                        style={{ width: 'auto', minWidth: `${col.label.length * 6 + 36}px`, cursor: 'pointer' }}
                                                        InputProps={{
                                                            disableUnderline: true,
                                                            readOnly: true,
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                        {Array.isArray(columnFilters[col.key]) && columnFilters[col.key].length > 0 && (
                                                                            <span className={styles["filter-count-badge"]}>
                                                                                {columnFilters[col.key].length}
                                                                            </span>
                                                                        )}
                                                                        {enableSorting && col.sortable !== false ? (
                                                                            <TableSortLabel
                                                                                active={sortConfig.key === col.key}
                                                                                direction={sortConfig.key === col.key ? sortConfig.direction : "asc"}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleSort(col.key);
                                                                                }}
                                                                                className={styles["sort-label-inline"]}
                                                                                IconComponent={SortIconComponent}
                                                                            />
                                                                        ) : null}
                                                                    </div>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                    <Popper
                                                        open={dropdownOpen[col.key] || false}
                                                        anchorEl={anchorRefs.current[col.key]}
                                                        placement="bottom-start"
                                                        style={{ zIndex: 1300 }}
                                                        modifiers={[
                                                            {
                                                                name: 'offset',
                                                                options: {
                                                                    offset: [0, 4],
                                                                },
                                                            },
                                                        ]}
                                                    >
                                                        <ClickAwayListener 
                                                            onClickAway={(event) => {
                                                                // Don't close if clicking on the anchor element (TextField) or inside any dropdown
                                                                if (anchorRefs.current[col.key] && anchorRefs.current[col.key].contains(event.target)) {
                                                                    return;
                                                                }
                                                                // Check if clicking inside any other dropdown
                                                                const clickedInsideDropdown = Object.keys(anchorRefs.current).some(key => {
                                                                    const ref = anchorRefs.current[key];
                                                                    return ref && ref !== anchorRefs.current[col.key] && ref.contains(event.target);
                                                                });
                                                                if (clickedInsideDropdown) {
                                                                    return;
                                                                }
                                                                // Close all dropdowns
                                                                setDropdownOpen({});
                                                            }}
                                                        >
                                                            <Paper className={styles["dropdown-paper"]}>
                                                                <div className={styles["dropdown-header"]}>
                                                                    <TextField
                                                                        size="small"
                                                                        placeholder={`Search ${col.label}`}
                                                                        value={dropdownSearch[col.key] || ""}
                                                                        onChange={(e) => handleDropdownSearchChange(col.key, e.target.value)}
                                                                        className={styles["dropdown-search-input"]}
                                                                        InputProps={{
                                                                            startAdornment: (
                                                                                <InputAdornment position="start">
                                                                                    <GlobalIcon iconName="SearchIcon" className={styles["search-icon"]} />
                                                                                </InputAdornment>
                                                                            ),
                                                                            disableUnderline: true,
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </div>
                                                                <DropdownList
                                                                    columnKey={col.key}
                                                                    allValues={uniqueColumnValues[col.key] || []}
                                                                    searchText={dropdownSearch[col.key] || ''}
                                                                    selectedValues={Array.isArray(columnFilters[col.key]) ? columnFilters[col.key] : []}
                                                                    onToggle={handleDropdownValueToggle}
                                                                />
                                                            </Paper>
                                                        </ClickAwayListener>
                                                    </Popper>
                                                </>
                                            ) : (
                                            <TextField
                                                size="small"
                                                placeholder={col.label}
                                                value={columnFilters[col.key] || ""}
                                                onChange={(e) => handleColumnFilterChange(col.key, e.target.value)}
                                                className={styles["column-filter-input"]}
                                                style={{ width: 'auto', minWidth: `${col.label.length * 6 + 36}px` }}
                                                InputProps={{
                                                    disableUnderline: true,
                                                    endAdornment: enableSorting && col.sortable !== false ? (
                                                        <InputAdornment position="end">
                                                            <TableSortLabel
                                                                active={sortConfig.key === col.key}
                                                                direction={sortConfig.key === col.key ? sortConfig.direction : "asc"}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSort(col.key);
                                                                }}
                                                                className={styles["sort-label-inline"]}
                                                                IconComponent={SortIconComponent}
                                                            />
                                                        </InputAdornment>
                                                    ) : null,
                                                }}
                                            />
                                            )}
                                        </div>
                                    ) : (
                                        <div className={styles["header-cell-content"]}>
                                            {enableSorting && col.sortable !== false ? (
                                                <TableSortLabel
                                                    active={sortConfig.key === col.key}
                                                    direction={sortConfig.key === col.key ? sortConfig.direction : "asc"}
                                                    onClick={() => handleSort(col.key)}
                                                    className={styles["sort-label"]}
                                                    IconComponent={SortIconComponent}
                                                >
                                                    {col.label}
                                                </TableSortLabel>
                                            ) : (
                                                <span>{col.label}</span>
                                            )}
                                        </div>
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length + (enableSelection ? 1 : 0)}
                                    className={styles["empty-cell"]}
                                >
                                    No data available
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedData.map((row) => {
                                const rowId = row[rowKey];
                                const isSelected = isRowSelected(rowId);
                                return (
                                    <TableRow
                                        key={rowId}
                                        className={`${styles["data-row"]} ${isSelected ? styles["selected-row"] : ""}`}
                                        hover
                                    >
                                        {enableSelection && (
                                            <TableCell className={styles["checkbox-cell"]} padding="checkbox">
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => handleSelectRow(rowId)}
                                                    className={styles["checkbox"]}
                                                    sx={{
                                                        '&.Mui-checked': {
                                                            color: 'rgba(79, 70, 229, 0.60)',
                                                        },
                                                        '&.Mui-checked .MuiSvgIcon-root': {
                                                            fill: 'rgba(79, 70, 229, 0.60)',
                                                            color: 'rgba(79, 70, 229, 0.60)',
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                        )}
                                        {columns.map((col) => {
                                            const cellStyle = getCellStyle(col, row[col.key], row);
                                            return (
                                                <TableCell 
                                                    key={col.key} 
                                                    className={styles["data-cell"]}
                                                    style={cellStyle}
                                                >
                                                    {renderCellValue(col, row)}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}

export default CustomTable;
